import { useQuery } from "@apollo/client";
import { Box, MenuItem } from "@mui/material";
import {
  Form,
  Formik,
  FormikHelpers,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import { useConfirm } from "material-ui-confirm";
import { enqueueSnackbar } from "notistack";
import { ReactElement, ReactNode } from "react";
import * as Yup from "yup";

import {
  CampaignStatus,
  ListActiveOwnedPhoneNumbersQuery,
  ListActiveOwnedPhoneNumbersQueryVariables,
  MessagingStrategyAvailability,
  MessagingStrategyName,
  ResetCampaignContactImportJobMutation,
  ResetCampaignContactImportJobMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import Select from "~frontend/components/forms/FormSelect";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES } from "~frontend/components/personalizedGifs/types";
import { RESET_CAMPAIGN_CONTACT_UPLOAD_JOB } from "~frontend/graphql/campaigns";
import { LIST_ACTIVE_OWNED_PHONE_NUMBERS } from "~frontend/graphql/ownedPhoneNumber";
import { SubaccountSupervolunteerData } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { StepInstructions } from "./StepInstructions";
import { FormikSaveButtons } from "./campaignEditorUtils/SaveButtons";
import {
  messagingStrategyComponents,
  useMessagingStrategyMutation,
} from "./messagingStrategyConfig";
import { CampaignForEditor } from "./types";

type Values = {
  [submitErrorField]?: string | null;
  goToNext?: boolean;
  messagingStrategy: {
    strategy: MessagingStrategyName;
    config: unknown;
  };
};

function MessagingStrategy({ campaign }: { campaign: CampaignForEditor }) {
  const formik = useFormikContext<Values>();
  const fieldProps = formik.getFieldProps("messagingStrategy.strategy");
  const subaccount = useSubaccountAdminConfig()!;

  const confirm = useConfirm();

  function strategyType(
    strategyName: MessagingStrategyName
  ): "shortcode" | "longcode" {
    const strat = subaccount.messagingStrategies.find(
      s => s.name === strategyName
    );
    return strat?.isShortCodeStrategy ? "shortcode" : "longcode";
  }

  const [resetCampaignContactImportJob] = useMutation<
    ResetCampaignContactImportJobMutation,
    ResetCampaignContactImportJobMutationVariables
  >(RESET_CAMPAIGN_CONTACT_UPLOAD_JOB);

  const { loading, data, error } = useQuery<
    ListActiveOwnedPhoneNumbersQuery,
    ListActiveOwnedPhoneNumbersQueryVariables
  >(LIST_ACTIVE_OWNED_PHONE_NUMBERS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  if (loading) {
    return (
      <Select value="" disabled>
        <MenuItem value="">Loading...</MenuItem>
      </Select>
    );
  }

  function resetStagedCampaignContactsFn(campaignId: string) {
    return async () => {
      if ((campaign.contactImportJob?.result?.nContacts ?? 0) > 0) {
        const { confirmed } = await confirm({
          title: "Confirm clear contacts",
          description: "Are you sure? This will delete any existing contacts.",
        });
        if (!confirmed) {
          return false;
        }
      }

      const { errors } = await resetCampaignContactImportJob({
        variables: {
          input: {
            campaignId,
          },
        },
      });

      if (errors) {
        return false;
      }
      return true;
    };
  }

  const ownedPhoneNumbers = data!.listActiveOwnedPhoneNumbers;
  const ownedPhoneNumberPools = subaccount.ownedPhoneNumberPools;
  const resetStagedCampaignContacts = resetStagedCampaignContactsFn(
    campaign.id
  );

  const handleChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
    const oldStrategy = fieldProps.value;
    const strategy = event.target.value as MessagingStrategyName;

    if (strategyType(oldStrategy) != strategyType(strategy)) {
      // Clear out any staged contacts
      if (!(await resetStagedCampaignContacts())) {
        // exit this function if they cancel the confirm
        return;
      }
    }

    const newComponent = messagingStrategyComponents[strategy];
    formik
      .setFieldValue(
        "messagingStrategy.config",
        newComponent.defaultFormValues({
          campaign,
          subaccount,
          ownedPhoneNumbers,
          ownedPhoneNumberPools,
        })
      )
      .catch(console.error);

    fieldProps.onChange(event);
  };

  const { Component } =
    messagingStrategyComponents[formik.values.messagingStrategy.strategy];

  const availableStrategies = subaccount.messagingStrategies.filter(s => {
    if (!s.isConfigured || !s.isEnabled) {
      return false;
    }

    if (campaign.startedAt && s.isShortCodeStrategy) {
      // Non-shortcode campaigns cannot be changed to shortcode after launch,
      // because we scrub your contacts to remove non-shortcode subscribers
      // at launch.
      //
      // For started shortcode campaigns, we won't hit this because we use
      // ReadOnlyMessagingStrategy instead of MessagingStrategy.
      return false;
    }

    return true;
  });

  const currentStrategy = formik.values.messagingStrategy.strategy;
  let currentButInvalidMessagingStrategy:
    | MessagingStrategyAvailability
    | undefined = undefined;
  if (
    currentStrategy &&
    !availableStrategies.find(s => s.name === currentStrategy)
  ) {
    currentButInvalidMessagingStrategy = subaccount.messagingStrategies.find(
      s => s.name === currentStrategy
    );
  }

  return (
    <>
      <Select
        fullWidth
        label="Phone Number Type"
        name={fieldProps.name}
        value={fieldProps.value}
        onChange={handleChange}
        onBlur={fieldProps.onBlur}
        error={!!formik.errors.messagingStrategy?.strategy}
        helperText={formik.errors.messagingStrategy?.strategy}
      >
        {currentButInvalidMessagingStrategy && (
          <MenuItem disabled value={currentButInvalidMessagingStrategy.name}>
            {currentButInvalidMessagingStrategy.displayName} (not available)
          </MenuItem>
        )}
        {availableStrategies.map(strategy => (
          <MenuItem value={strategy.name} key={strategy.name}>
            {strategy.displayName}
          </MenuItem>
        ))}
      </Select>
      <Component
        ownedPhoneNumbers={ownedPhoneNumbers}
        ownedPhoneNumberPools={ownedPhoneNumberPools}
        campaign={campaign}
      />
    </>
  );
}

function MessagingStrategyReadOnly({
  campaign,
}: {
  campaign: CampaignForEditor;
}) {
  const subaccount = useSubaccountAdminConfig()!;
  const { Component } =
    messagingStrategyComponents[campaign.messagingStrategy.strategy];

  return (
    <>
      <Select
        fullWidth
        label="Phone Number Strategy"
        name="messagingStrategy.strategy"
        value={campaign.messagingStrategy.strategy}
        disabled
      >
        <MenuItem value={campaign.messagingStrategy.strategy}>
          {
            subaccount.messagingStrategies.find(
              s => s.name === campaign.messagingStrategy.strategy
            )?.displayName
          }
        </MenuItem>
      </Select>
      <Component
        savedConfig={campaign.messagingStrategy.config as any}
        campaign={campaign}
        readOnly
      />
    </>
  );
}

function getInitialValues(
  campaign: Pick<CampaignForEditor, "messagingStrategy">
): Values {
  const component =
    messagingStrategyComponents[campaign.messagingStrategy.strategy];

  return {
    messagingStrategy: {
      strategy: campaign.messagingStrategy.strategy,
      config: component.campaignConfigToFormValues(
        campaign.messagingStrategy.config as any
      ),
    },
  };
}

export default function SendingNumbers({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const messagingStrategyMutation = useMessagingStrategyMutation();
  const formatError = useErrorMessageFormatter();

  const campaignHasPersonalizedGifs =
    campaign.scriptVariants.filter(
      variant => variant.personalizedGif !== null && variant.deletedAt === null
    ).length > 0;

  const onValidate = (values: Values) => {
    if (
      campaignHasPersonalizedGifs &&
      !VALID_PERSONALIZED_GIF_MESSAGING_STRATEGIES.includes(
        values.messagingStrategy.strategy
      )
    ) {
      return {
        messagingStrategy: {
          strategy:
            "Must select Bandwidth phone numbers when scripts use personalized gifs",
        },
      };
    }

    let messagingStrategyValidator = Yup.mixed();
    if (values.messagingStrategy && values.messagingStrategy.strategy) {
      const component =
        messagingStrategyComponents[values.messagingStrategy.strategy];
      if (component.configValidationSchema) {
        messagingStrategyValidator = component.configValidationSchema;
      }
    }

    const schema = Yup.object({
      messagingStrategy: Yup.object({
        strategy: Yup.string().required(),
        config: messagingStrategyValidator,
      }),
    });

    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      if (e instanceof Error && e.name === "ValidationError") {
        return yupToFormErrors(e);
      }
      throw e;
    }
  };

  const handleSave = async (
    { messagingStrategy, goToNext }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    const { errors, data } = await messagingStrategyMutation({
      campaignId: campaign.id,
      strategy: {
        strategy: messagingStrategy.strategy,
        config: messagingStrategyComponents[
          messagingStrategy.strategy
        ].formValuesToConfigInput(messagingStrategy.config as any) as any,
      },
    });

    setSubmitting(false);

    if (errors) {
      enqueueSnackbar(`Failed to save campaign: ${formatError(errors)}`, {
        variant: "error",
      });
    } else {
      resetForm({
        values: getInitialValues({
          messagingStrategy: data!,
        }),
      });

      if (goToNext) {
        goToNextSection({ bypassRouteLeavingGuard: true });
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  const canEdit = campaign.usingShortCodeStrategy
    ? campaign.startedAt === null
    : [
        CampaignStatus.Draft,
        CampaignStatus.InitialSendReady,
        CampaignStatus.InitialSendScheduled,
        CampaignStatus.RoiTestAutosendPending,
      ].includes(campaign.status);

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <StepInstructions header="Choose a phone number for sending texts" />
      </Box>
      <Formik
        initialValues={getInitialValues(campaign)}
        validate={onValidate}
        onSubmit={handleSave}
      >
        {({ dirty }) => {
          return (
            <Form>
              <RouteLeavingGuard when={dirty} />
              <FormikErrorBanner />
              {!canEdit ? (
                <MessagingStrategyReadOnly campaign={campaign} />
              ) : (
                <MessagingStrategy campaign={campaign} />
              )}
              <FormikSaveButtons continueWithoutSaving={goToNextSection} />
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

export function validateSendingNumbers({
  campaign,
  subaccount,
}: {
  campaign: CampaignForEditor;
  subaccount: SubaccountSupervolunteerData;
}): { title: string; message: ReactNode }[] {
  const strategy = subaccount.messagingStrategies.find(
    s => s.name === campaign.messagingStrategy.strategy
  );
  if (!strategy || !strategy.isConfigured || !strategy.isEnabled) {
    return [
      {
        title: "Sending Numbers are invalid",
        message:
          "You must change your Sending Numbers before starting your campaign.",
      },
    ];
  }

  return [];
}
